import React, { useState, useRef, useEffect } from "react";
import clsx from "clsx";
import loadable from "@loadable/component";
import useFetch from "@micado-digital/react-ui/hooks/useFetch";
import getCurrentSeason from "./utils/getCurrentSeason";
import useCurrentWeather from "@micado-digital/react-weather/hooks/useCurrentWeather";
import Season from "./Season";
import Weather from "./Weather";
import Time from "./Time";

import styles from "./VideoPlayer.styles";

const SyncLoader = loadable(() => import("react-spinners/SyncLoader"));
const ReactPlayer = loadable(() => import("react-player"));
const ReactSVGIcon = loadable(() => import("@micado-digital/react-svg-icon/ReactSVGIcon"));

const VideoPlayer = ({ defaultSeason = "Winter", folder, lang = "de" }) => {
	const { REACT_APP_PATH } = process.env;
	const [season, setSeason] = useState(defaultSeason);
	const [time, setTime] = useState("Mittag");
	const [weather, setWeather] = useState("Sonnig");
	const [highlights, setHighlights] = useState(false);
	const [fileName, setFileName] = useState("");
	const [timePlayed, setTimePlayed] = useState(0);
	const [videoItems, setVideoItems] = useState([]);
	const [videoEnded, setVideoEnded] = useState(false);
	const [currentVideoIndex, setCurrentVideoIndex] = useState(0);
	const [totalVideos, setTotalVideos] = useState(0);
	// const [loading, setLoading] = useState(false);
	const css = styles();
	const ref = useRef();

	const videos = useFetch(
		`${REACT_APP_PATH}/Micado.WebEngine/Micado.WebEngine.Plugin.Media/List.api?FolderNames=${folder}&Types=11&Sort=1`
	);
	const { items, entries } = videos;

	const currentWeather = useCurrentWeather({
		lang: "de",
		station: "7",
		url: "https://www.wagrain-kleinarl.at/SkigebieteManager/Micado.SkigebieteManager.Plugin.WeatherApi/GetDetails.api"
	});

	useEffect(() => {
		if (!defaultSeason) {
			setSeason(getCurrentSeason());
		}
	}, [defaultSeason]);

	console.log(season, time, weather);

	useEffect(() => {
		if (currentWeather.state) {
			switch (currentWeather.state) {
				case "A":
				case "B":
				case "C":
					setWeather("Sonnig");
					break;

				case "D":
				case "E":
				case "F":
				case "K":
				case "Y":
				case "Z":
					setWeather("Bewölkt");
					break;

				case "G":
				case "H":
				case "I":
				case "J":
				case "L":
				case "M":
				case "N":
				case "O":
				case "P":
				case "Q":
				case "R":
				case "U":
				case "V":
				case "W":
				case "X":
					setWeather("Wechselhaft");
					break;

				case "S":
				case "T":
					setWeather("Nebelig");
					break;

				default:
					setWeather("Sonnig");
					break;
			}
		}
	}, [currentWeather.state]);

	useEffect(() => {
		if (entries > 0) {
			if (highlights) {
				const filteredArray = items.filter(item => {
					if (item?.keywords) {
						return (
							item.keywords.some(keyword => keyword.title === season) &&
							item.keywords.some(keyword => keyword.title === "Highlight")
						);
					}
					return null;
				});

				setTotalVideos(filteredArray.length);
				setVideoItems(filteredArray);
			} else {
				if (time === "Nacht") {
					const filteredArray = items.filter(item => {
						if (item?.keywords) {
							return (
								item.keywords.some(keyword => keyword.title === season) &&
								item.keywords.some(keyword => keyword.title === time)
							);
						}
						return null;
					});
					setVideoItems(filteredArray);
				} else {
					const filteredArray = items.filter(item => {
						if (item?.keywords) {
							return (
								item.keywords.some(keyword => keyword.title === season) &&
								item.keywords.some(keyword => keyword.title === weather) &&
								item.keywords.some(keyword => keyword.title === time)
							);
						}
						return null;
					});
					setVideoItems(filteredArray);
				}
			}
		}
	}, [items, entries, season, time, weather, highlights]);

	useEffect(() => {
		if (videoItems?.length > 0) {
			const file = videoItems.map(item => {
				if (highlights) {
					return REACT_APP_PATH + item?.media?.filename;
				}
				return item?.media?.filename;
			});

			if (highlights && Array.isArray(file) && file.length > 0) {
				setFileName(file);
			} else if (file) {
				setFileName(REACT_APP_PATH + file[0]);
			} else {
				setFileName(REACT_APP_PATH + items?.[0]?.media?.filename);
			}
		} else {
			setFileName(REACT_APP_PATH + items?.[0]?.media?.filename);
		}
	}, [REACT_APP_PATH, videoItems, highlights, items]);

	const handleSeasonClick = (event, keyword) => {
		event.preventDefault();

		setSeason(keyword);
		// setWeather("Sonnig");
		setTime("Mittag");
		setTimePlayed(0);
		// if (videoEnded) {
		// 	setTimePlayed(0);
		// } else {
		// 	setTimePlayed(ref?.current?.getCurrentTime());
		// }
		setVideoEnded(false);
	};

	const handleWeatherClick = (event, keyword) => {
		event.preventDefault();

		setWeather(keyword);
		if (videoEnded) {
			setTimePlayed(0);
		} else {
			setTimePlayed(ref?.current?.getCurrentTime());
		}
		setVideoEnded(false);
	};

	const handleTimeClick = (event, keyword) => {
		event.preventDefault();

		setTime(keyword);

		if (videoEnded) {
			setTimePlayed(0);
		} else {
			setTimePlayed(ref?.current?.getCurrentTime());
		}
		setVideoEnded(false);
	};

	const handleRestart = () => {
		setTimePlayed(0);
		ref?.current?.seekTo(0.0, "seconds");
		setSeason(getCurrentSeason());
		setHighlights(false);
	};

	const handleVideoEnd = () => {
		// play multiple videos if highlights ar activated

		if (highlights) {
			let currentFileName;
			if (Array.isArray(fileName)) {
				currentFileName = fileName[0];
			} else {
				currentFileName = fileName;
			}

			const currentIndex = videoItems.findIndex(
				item => REACT_APP_PATH + item?.media?.filename === currentFileName
			);

			const nextIndex = currentIndex + 1;
			if (nextIndex < videoItems.length) {
				setFileName(REACT_APP_PATH + videoItems[nextIndex].media.filename);
				setTimePlayed(0);
			}
		} else {
			setVideoEnded(true);
		}
	};

	const handleStart = seconds => {
		if (seconds) {
			ref?.current?.seekTo(0.0, "seconds");
		} else {
			ref?.current?.seekTo(timePlayed, "seconds");
		}
	};

	const handleHighlights = () => {
		setHighlights(!highlights);
	};

	const handleNextVideo = () => {
		const nextIndex = (currentVideoIndex + 1) % totalVideos;
		setCurrentVideoIndex(nextIndex);

		const nextVideo = videoItems[nextIndex];
		setFileName(REACT_APP_PATH + nextVideo?.media?.filename);
	};

	const handlePrevVideo = () => {
		const prevIndex = (currentVideoIndex - 1 + totalVideos) % totalVideos;
		setCurrentVideoIndex(prevIndex);

		const prevVideo = videoItems[prevIndex];
		setFileName(REACT_APP_PATH + prevVideo?.media?.filename);
	};

	return (
		<div className={clsx(css.root, "video-player-root")}>
			<div className={clsx(css.videoWrapper, "video-player-root__video-wrapper")}>
				{fileName || fileName?.length > 0 ? (
					<ReactPlayer
						url={fileName}
						controls
						height="100%"
						width="100%"
						loop={false}
						muted={true}
						playing
						ref={ref}
						onEnded={() => handleVideoEnd()}
						onStart={() => handleStart()}
					/>
				) : (
					<SyncLoader color="#E62850" />
				)}
				{highlights && videoItems?.length > 1 && (
					<div className={css.navigation}>
						<div
							className={clsx(css.nav, "video-player-root__nav--prev")}
							onClick={handlePrevVideo}
						>
							<ReactSVGIcon
								color="initial"
								src="/img/icons/videos/chevron-left.svg"
								size={40}
							/>
						</div>
						<div
							className={clsx(css.nav, "video-player-root__nav--next")}
							onClick={handleNextVideo}
						>
							<ReactSVGIcon
								color="initial"
								src="/img/icons/videos/chevron-right.svg"
								size={40}
							/>
						</div>
					</div>
				)}
			</div>
			<div
				className={clsx(css.options, "video-player-root__options", {
					[css.optionsHighlights]: highlights
				})}
			>
				<Season
					handleSeasonClick={handleSeasonClick}
					items={items}
					lang={lang}
					season={season}
				/>
				<Weather
					handleWeatherClick={handleWeatherClick}
					items={items}
					lang={lang}
					season={season}
					weather={weather}
				/>
				<Time
					handleTimeClick={handleTimeClick}
					items={items}
					lang={lang}
					season={season}
					weather={weather}
					time={time}
				/>
				<div className={clsx(css.controls, "video-player-root__controls")}>
					<div className={clsx(css.controlsItem, "video-player-root__controls-item")}>
						<div
							className={clsx(
								css.button,
								"video-player-root__controls-restart",
								"video-player-root__buttons-button",
								{ [css.controlsButtonActive]: highlights }
							)}
							onClick={() => handleHighlights()}
						>
							<ReactSVGIcon color="initial" src="/img/icons/videos/highlight.svg" size={24} />
						</div>
						<span className={clsx(css.label, "video-player-root__buttons-label")}>
							Highlights
						</span>
					</div>
					<div className={clsx(css.controlsItem, "video-player-root__controls-item")}>
						<div
							className={clsx(
								css.button,
								"video-player-root__controls-restart",
								"video-player-root__buttons-button"
							)}
							onClick={() => handleRestart()}
						>
							<ReactSVGIcon color="initial" src="/img/icons/videos/restart.svg" size={24} />
						</div>
						<span className={clsx(css.label, "video-player-root__buttons-label")}>
							{lang === "de" ? "Zurücksetzen" : "reset"}
						</span>
					</div>
				</div>
			</div>
		</div>
	);
};

export default VideoPlayer;
